import { isObjectValid } from 'infrastructure/functions';
import {
  CONDITIONS_PER_PAGE,
  useAddCondition,
  useEditCondition,
  useGetConditions,
} from 'infrastructure/hooks/ccm';
import { useDeleteCondition } from 'infrastructure/hooks/ccm/patient-goals/use-delete-condition';
import { useTrackingPlatform } from 'infrastructure/hooks/patient/use-tracking-platform';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { SortState } from 'infrastructure/types';
import type { Values } from '../admin-panel/forms/patient-condition/validation-schema';
import type { IConditionForUpdate } from 'infrastructure/interfaces';
import type { FormikHelpers } from 'formik';

interface Props {
  patientGuid: string;
  isAdminPanel?: boolean;
}

export const useCondition = ({ patientGuid, isAdminPanel }: Props) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const [dateTimeSort, setDateTimeSort] = useState<SortState>('');
  const { t } = useTranslation();
  const { goals, isLoading, refetch } = useGetConditions({
    patientGuid,
    documentationTimeSort,
    dateTimeSort,
    meta: { items: CONDITIONS_PER_PAGE, page },
  });
  const [conditionForUpdate, setConditionForUpdate] =
    useState<IConditionForUpdate>();
  const [modifyItemGuid, setModifyItemGuid] = useState<string>();
  const { addCondition, addLoading } = useAddCondition({ page });
  const { editCondition, editLoading } = useEditCondition({ page });
  const { deleteCondition, deleteLoading } = useDeleteCondition({
    page,
    patientGuid,
  });
  const { Alert, showAlert } = useAlert();
  const { showTrackingPlatform, getTrackingPlatform } = useTrackingPlatform({
    patientGuid,
  });

  useEffect(() => {
    if (!isLoading && page > 1 && goals?.items?.length === 0) {
      setPage((prev) => prev - 1);
    }
  }, [goals, isLoading, page]);

  const toggleOpen = () => setOpen((prev) => !prev);

  const onCancelForm = () => {
    setOpen(false);
    setConditionForUpdate(undefined);
  };

  const onSubmit = (values: Values, formikHelpers: FormikHelpers<Values>) => {
    const valuesToCheck = {
      code: values.code,
      description: values.description,
      diagnoseGuid: values.diagnoseGuid,
    };

    if (!isObjectValid(valuesToCheck)) return;

    const { description, diagnoseGuid } = valuesToCheck;
    const resolvedTrackingPlatform = getTrackingPlatform(
      values.trackingPlatform,
    );

    if (conditionForUpdate) {
      setModifyItemGuid(conditionForUpdate.guid);
      editCondition(
        {
          description,
          diagnoseGuid,
          guid: conditionForUpdate.guid,
          patientGuid,
          actionFrom: isAdminPanel ? 'admin-panel' : undefined,
        },
        {
          onSuccess: () => {
            refetch();
            formikHelpers.resetForm();
            onCancelForm();
          },
        },
      );
      return;
    }

    addCondition(
      {
        description,
        diagnoseGuid,
        patientGuid,
        trackingPlatform: resolvedTrackingPlatform,
        actionFrom: isAdminPanel ? 'admin-panel' : undefined,
      },
      {
        onSuccess: () => {
          refetch();
          formikHelpers.resetForm();
          onCancelForm();
        },
      },
    );
  };

  const handleDelete = async (guid: string) => {
    setModifyItemGuid(guid);
    const res = await showAlert({
      title: t('confirm.deleteCondition'),
      message: t('confirm.deleteConditionMessage'),
      messageTitle: t('confirm.areYouSureYouWantDeleteThis', {
        value: t('labels.condition'),
      }),
      type: 'danger',
    });
    if (res) {
      deleteCondition(
        { guid, actionFrom: isAdminPanel ? 'admin-panel' : undefined },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
      setConditionForUpdate(undefined);
    }
  };

  return {
    open,
    page,
    goals,
    isLoading,
    modifyItemGuid,
    addLoading,
    editLoading,
    deleteLoading,
    Alert,
    conditionForUpdate,
    documentationTimeSort,
    dateTimeSort,
    setPage,
    setOpen,
    handleDelete,
    toggleOpen,
    onSubmit,
    setConditionForUpdate,
    onCancelForm,
    setDocumentationTimeSort,
    setDateTimeSort,
    showTrackingPlatform,
  };
};
